import React from 'react';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <SEO 
        title={t('privacy.seo_title') || "Politica de Confidențialitate - Oana Tenea"}
        description={t('privacy.seo_desc') || "Află cum protejăm datele tale personale și medicale conform normelor GDPR și eticii profesionale."}
        path="/politica-confidentialitate"
      />
      
      <div className="bg-stone-50 dark:bg-stone-950 text-stone-800 dark:text-stone-200 py-16 sm:py-24 transition-colors duration-300">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          
          {/* Header */}
          <div className="text-center mb-16">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 text-xs font-semibold tracking-wider uppercase mb-4 border border-stone-200 dark:border-stone-700">
              Confidențialitate & Securitate
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-stone-900 dark:text-stone-100 tracking-tight mb-4">
              {t('privacy.header.title')}
            </h1>
            <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              {t('privacy.header.subtitle')}
            </p>
            <div className="w-16 h-1 bg-terracotta mx-auto mt-6 rounded-full"></div>
          </div>

          {/* Content Card */}
          <div className="bg-white dark:bg-stone-900 rounded-3xl shadow-elevated p-8 sm:p-12 border border-stone-200/80 dark:border-stone-800 space-y-12">
            
            {/* Introducere */}
            <section className="border-b border-stone-100 dark:border-stone-800/80 pb-10">
              <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                  1
                </div>
                {t('privacy.intro.title')}
              </h2>
              <div className="text-stone-700 dark:text-stone-300 leading-relaxed text-sm sm:text-base space-y-4">
                <p dangerouslySetInnerHTML={{ __html: t('privacy.intro.p1') }} />
                <p dangerouslySetInnerHTML={{ __html: t('privacy.intro.p2') }} />
                <div className="bg-stone-50 dark:bg-stone-800/60 border-l-4 border-terracotta p-4 rounded-r-2xl mt-4">
                  <p className="text-xs font-semibold text-stone-900 dark:text-stone-100 mb-1">{t('privacy.intro.date')}</p>
                  <p className="text-xs text-stone-600 dark:text-stone-400">{t('privacy.intro.applies')}</p>
                </div>
              </div>
            </section>

            {/* Date colectate */}
            <section className="border-b border-stone-100 dark:border-stone-800/80 pb-10">
              <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                  2
                </div>
                {t('privacy.data_collected.title')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-stone-50 dark:bg-stone-800/60 p-6 rounded-2xl border border-stone-200/60 dark:border-stone-700">
                  <h3 className="text-base font-bold text-stone-900 dark:text-stone-100 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {t('privacy.data_collected.id_title')}
                  </h3>
                  <ul className="text-stone-600 dark:text-stone-400 text-sm space-y-1.5">
                    {Array.isArray(t('privacy.data_collected.id_list', { returnObjects: true })) &&
                      t('privacy.data_collected.id_list', { returnObjects: true }).map((item, i) => (
                        <li key={i} className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2 mt-1.5 flex-shrink-0"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                  </ul>
                </div>
                
                <div className="bg-stone-50 dark:bg-stone-800/60 p-6 rounded-2xl border border-stone-200/60 dark:border-stone-700">
                  <h3 className="text-base font-bold text-stone-900 dark:text-stone-100 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('privacy.data_collected.clinical_title')}
                  </h3>
                  <ul className="text-stone-600 dark:text-stone-400 text-sm space-y-1.5">
                    {Array.isArray(t('privacy.data_collected.clinical_list', { returnObjects: true })) &&
                      t('privacy.data_collected.clinical_list', { returnObjects: true }).map((item, i) => (
                        <li key={i} className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2 mt-1.5 flex-shrink-0"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Scopul procesării */}
            <section className="border-b border-stone-100 dark:border-stone-800/80 pb-10">
              <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                  3
                </div>
                {t('privacy.purpose.title')}
              </h2>
              <div className="space-y-4">
                {Array.isArray(t('privacy.purpose.items', { returnObjects: true })) &&
                  t('privacy.purpose.items', { returnObjects: true }).map((item, i) => (
                    <div key={i} className="bg-stone-50 dark:bg-stone-800/60 p-5 rounded-2xl border border-stone-200/60 dark:border-stone-700">
                      <h3 className="text-base font-bold text-stone-900 dark:text-stone-100 mb-1">{item.title}</h3>
                      <p className="text-stone-600 dark:text-stone-400 text-sm">{item.desc}</p>
                    </div>
                  ))}
              </div>
            </section>

            {/* Baza legală */}
            <section className="border-b border-stone-100 dark:border-stone-800/80 pb-10">
              <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                  4
                </div>
                {t('privacy.legal_basis.title')}
              </h2>
              <div className="bg-stone-50 dark:bg-stone-800/60 p-6 rounded-2xl border border-stone-200/60 dark:border-stone-700 space-y-4">
                {Array.isArray(t('privacy.legal_basis.items', { returnObjects: true })) &&
                  t('privacy.legal_basis.items', { returnObjects: true }).map((item, i) => (
                    <div key={i} className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-terracotta mt-2 mr-3 flex-shrink-0"></div>
                      <div>
                        <h3 className="font-bold text-stone-900 dark:text-stone-100 text-sm sm:text-base">{item.title}</h3>
                        <p className="text-stone-600 dark:text-stone-400 text-xs sm:text-sm mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </section>

            {/* Securitatea datelor */}
            <section className="border-b border-stone-100 dark:border-stone-800/80 pb-10">
              <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                  5
                </div>
                {t('privacy.security.title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Array.isArray(t('privacy.security.items', { returnObjects: true })) &&
                  t('privacy.security.items', { returnObjects: true }).map((item, i) => (
                    <div key={i} className="p-5 bg-stone-50 dark:bg-stone-800/60 rounded-2xl border border-stone-200/60 dark:border-stone-700 text-center">
                      <div className="w-10 h-10 bg-terracotta/10 text-terracotta rounded-xl mx-auto mb-3 flex items-center justify-center">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <h3 className="font-bold text-stone-900 dark:text-stone-100 text-sm mb-1">{item.title}</h3>
                      <p className="text-xs text-stone-600 dark:text-stone-400">{item.desc}</p>
                    </div>
                  ))}
              </div>
            </section>

            {/* Drepturile GDPR */}
            <section className="border-b border-stone-100 dark:border-stone-800/80 pb-10">
              <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                  6
                </div>
                {t('privacy.rights.title')}
              </h2>
              <div className="space-y-3">
                {Array.isArray(t('privacy.rights.items', { returnObjects: true })) &&
                  t('privacy.rights.items', { returnObjects: true }).map((right, index) => (
                    <div key={index} className="bg-stone-50 dark:bg-stone-800/60 p-4 sm:p-5 rounded-2xl border border-stone-200/60 dark:border-stone-700">
                      <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100 mb-1">{right.title}</h3>
                      <p className="text-xs sm:text-sm text-stone-600 dark:text-stone-400">{right.description || right.desc}</p>
                    </div>
                  ))}
              </div>
            </section>

            {/* Perioada de păstrare */}
            <section className="border-b border-stone-100 dark:border-stone-800/80 pb-10">
              <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                  7
                </div>
                {t('privacy.retention.title')}
              </h2>
              <div className="bg-stone-50 dark:bg-stone-800/60 p-6 rounded-2xl border border-stone-200/60 dark:border-stone-700 space-y-3 text-sm">
                <div className="flex items-center text-stone-700 dark:text-stone-300">
                  <span className="font-bold mr-2">{t('privacy.retention.active')}:</span>
                  <span className="text-stone-600 dark:text-stone-400">{t('privacy.retention.active_desc')}</span>
                </div>
                <div className="flex items-center text-stone-700 dark:text-stone-300">
                  <span className="font-bold mr-2">{t('privacy.retention.archived')}:</span>
                  <span className="text-stone-600 dark:text-stone-400">{t('privacy.retention.archived_desc')}</span>
                </div>
                <div className="flex items-center text-stone-700 dark:text-stone-300">
                  <span className="font-bold mr-2">{t('privacy.retention.contact')}:</span>
                  <span className="text-stone-600 dark:text-stone-400">{t('privacy.retention.contact_desc')}</span>
                </div>
              </div>
            </section>

            {/* Contact și reclamații */}
            <section className="bg-gradient-to-br from-terracotta-50 to-amber-50/50 dark:from-stone-800 dark:to-stone-800/50 p-6 sm:p-8 rounded-3xl border border-terracotta-200/60 dark:border-stone-700">
              <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                  8
                </div>
                {t('privacy.contact.title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-stone-700 dark:text-stone-300">
                <div>
                  <h3 className="font-bold text-stone-900 dark:text-stone-100 mb-3">{t('privacy.contact.gdpr_title')}</h3>
                  <div className="space-y-1.5">
                    <p><strong>Email:</strong> {t('privacy.contact.email')}</p>
                    <p><strong>Adresa:</strong> {t('privacy.contact.address')}</p>
                    <p><strong>Timp de răspuns:</strong> {t('privacy.contact.response_time')}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-bold text-stone-900 dark:text-stone-100 mb-3">{t('privacy.contact.auth_title')}</h3>
                  <div className="space-y-1.5">
                    <p><strong>{t('privacy.contact.auth_name')}</strong> {t('privacy.contact.auth_desc')}</p>
                    <p><strong>{t('privacy.contact.auth_website')}</strong></p>
                    <p className="text-xs text-stone-500 dark:text-stone-400 mt-2">{t('privacy.contact.auth_complaint')}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer note */}
            <div className="text-center pt-4 text-xs text-stone-500 dark:text-stone-400">
              <p className="font-medium text-stone-700 dark:text-stone-300 mb-1">
                {t('privacy.footer.priority')}
              </p>
              <p>
                {t('privacy.footer.updates')}
              </p>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
